export type BrowserPushStatus = {
  registered: boolean; version: number; expiresAt?: string;
  lastDelivery?: {id: string; status: string; acknowledgedAt?: string | null};
};
export type BrowserPushConfig = {enabled: boolean; applicationServerKey?: string};
const BASE = "/api/v1/notifications/push";
const INSTANCE_KEY = "pharmhub:browser-push-instance";

export function browserPushSupported(): boolean {
  return window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}
function instance(): string {
  const saved = localStorage.getItem(INSTANCE_KEY);
  if (saved && /^[0-9a-f-]{36}$/.test(saved)) return saved;
  const value = crypto.randomUUID();
  localStorage.setItem(INSTANCE_KEY, value); // Random browser instance only, not a token/key.
  return value;
}
function csrf(): string {
  const value = document.cookie.split("; ").find((part) => part.startsWith("pharmhub_csrf="));
  return value ? decodeURIComponent(value.slice("pharmhub_csrf=".length)) : "";
}
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(BASE + path, {credentials: "same-origin", cache: "no-store", ...init});
  if (!response.ok) throw new Error("Уведомления недоступны. Обновите состояние и повторите попытку.");
  return response.json() as Promise<T>;
}
function mutate<T>(path: string, method: string, version: number, body: object): Promise<T> {
  return request<T>(path, {method, headers: {"Content-Type": "application/json", "X-CSRF-Token": csrf(),
    "Idempotency-Key": crypto.randomUUID(), "If-Match": String(version)}, body: JSON.stringify(body)});
}
export function browserPushConfig(): Promise<BrowserPushConfig> { return request("/config"); }
export function browserPushStatus(): Promise<BrowserPushStatus> {
  return request(`/subscription?instanceId=${encodeURIComponent(instance())}`);
}
function publicKey(value: string): Uint8Array<ArrayBuffer> {
  const raw = atob(value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}
export async function enableBrowserPush(config: BrowserPushConfig): Promise<BrowserPushStatus> {
  if (!browserPushSupported() || !config.enabled || !config.applicationServerKey) throw new Error("Push-уведомления пока недоступны.");
  // Must be called directly by a user's click, before any network await.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Разрешение на уведомления не получено. Его можно изменить в настройках браузера.");
  const registration = await navigator.serviceWorker.register("/pharmhub-push-sw.js", {scope: "/", updateViaCache: "none"});
  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  const serverKey = publicKey(config.applicationServerKey);
  if (subscription?.options.applicationServerKey &&
      Array.from(new Uint8Array(subscription.options.applicationServerKey)).join(",") !== Array.from(serverKey).join(",")) {
    await subscription.unsubscribe(); subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: serverKey});
  const current = await browserPushStatus();
  return mutate("/subscription", "PUT", current.version, {instanceId: instance(), subscription: subscription.toJSON()});
}
export async function disableBrowserPush(): Promise<BrowserPushStatus> {
  let result: BrowserPushStatus = {registered: false, version: 0};
  try {
    const current = await browserPushStatus();
    result = await mutate("/subscription", "DELETE", current.version, {instanceId: instance()});
  } finally {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration("/");
      await (await registration?.pushManager.getSubscription())?.unsubscribe();
      registration?.active?.postMessage({type: "pharmhub-push-clear"});
    }
  }
  return result;
}
export function testBrowserPush(version: number): Promise<BrowserPushStatus & {deliveryId: string}> {
  return mutate("/test", "POST", version, {instanceId: instance()});
}
export async function revokeBrowserPushBeforeLogout(): Promise<void> {
  if (!browserPushSupported()) return;
  try { await disableBrowserPush(); } catch { /* Always let logout revoke the server session. */ }
}
